import React, { ReactElement, useRef } from 'react'
import cx from 'classnames'
import BannerImage from '../banner-image'
import CrosshairLink from '../crosshair-link'
import * as styles from '../../styles/components/content-blocks/banner-image.module.scss'
import reactElementToString from '../../utils/react-element-to-string'
import { motion, useScroll, useTransform } from 'framer-motion'
import { bezierEaseIn } from '../../utils/styles'
import { FillColor } from '../../components/color-fill'
import { FeaturedLink } from '../../utils/types'

type ColorVariant = 'light' | 'dark'

interface CommonProps {
    featuredLink?: FeaturedLink
    /** Format: horizontalVertical justification*/
    altText?: string
    className?: string
}

export interface Content {
    title?: string
    body?: ReactElement | string
    image?: string
    colorFill?: FillColor
    textColorVariant?: ColorVariant
    textJustification?: 'centerCenter' | 'leftTop' | 'centerTop' | 'leftCenter'
}

export type BannerImageBlockProps = CommonProps & Content

export interface DoubleBannerImageBlockProps extends CommonProps {
    left: Content
    right: Content
}

interface BodyProps {
    content?: ReactElement | string
    colorVariant?: ColorVariant
}

// TODO: unify BannerImageBlock & DoubleBannerImageBlock components

export const BannerImageBlock = (props: BannerImageBlockProps) => {
    const {
        className, title, body, textJustification = 'centerCenter',
        featuredLink, image, colorFill, altText, textColorVariant = 'light',
    } = props
    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0.1, 0.7], [0, 1], { ease: bezierEaseIn })

    return (
        <motion.div
            ref={targetRef}
            className={cx(styles.container, className)}
            style={{ opacity }}
        >
            <BannerImage
                imageMode={'single'}
                imageSrc={image}
                colorFill={colorFill}
                wrapDoubleOnMobile={true}
                altText={altText}
                className={cx(styles.image)}
            />
            {(featuredLink || title || body) &&
                <div className={cx(styles.contentContainer, styles[textColorVariant])}>
                    <div className={cx(styles.content, styles[textJustification])}>
                        {title && <h2 className={styles.title}>{title}</h2>}
                        {body && <Body content={body} colorVariant={textColorVariant} />}
                    </div>
                    {featuredLink && <FLink data={featuredLink}/>}
                </div>
            }
        </motion.div>
    )
}

export const DoubleBannerImageBlock = (props: DoubleBannerImageBlockProps) => {
    const {
        className, left, right, featuredLink, altText,
    } = props

    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0, 0.7], [0, 1], { ease: bezierEaseIn })
    const hasRightContent = !!(right?.title || right?.body)

    return (
        <motion.div
            ref={targetRef}
            className={cx(styles.container, className, {
                [styles.doubleContainer]: hasRightContent,
                [styles.emptyRight]: !hasRightContent,
            })}
            style={{ opacity }}
        >
            <BannerImage
                imageMode={'double'}
                imageSrc={left.image}
                imageSrcSecondary={right.image}
                colorFill={left.colorFill}
                colorFillSecondary={right.colorFill}
                wrapDoubleOnMobile={hasRightContent}
                altText={altText}
                className={cx(styles.image)}
            />
            {(featuredLink || left?.title || right?.title || left?.body || right?.body) &&
                <div className={cx(styles.contentContainer, styles.doubleContentContainer, {
                    [styles.emptyRight]: !hasRightContent,
                })}>
                    <div
                        className={cx(
                            styles.content,
                            styles.contentLeft,
                            styles[left?.textJustification ?? 'centerCenter'],
                            styles[left?.textColorVariant ?? 'light'],
                        )}
                    >
                        {left?.title && <h2 className={styles.title}>{left.title}</h2>}
                        {left?.body && <Body content={left.body} colorVariant={left.textColorVariant} />}
                    </div>
                    {hasRightContent && <div
                        className={cx(
                            styles.content,
                            styles.contentRight,
                            styles[right?.textJustification ?? 'centerCenter'],
                            styles[right?.textColorVariant ?? 'light'],
                        )}
                    >
                        {right?.title && <h2 className={styles.title}>{right.title}</h2>}
                        {right?.body && <Body content={right.body} colorVariant={right.textColorVariant} />}
                    </div>}
                    {featuredLink && <FLink data={featuredLink}/>}
                </div>
            }
        </motion.div>
    )
}

const Body = ({ content, colorVariant }: BodyProps) => {
    if (!content) {
        return null
    }

    return (
        <div className={styles.body} dangerouslySetInnerHTML={{ __html: reactElementToString(content) }} />
    )
}

const FLink = (props: { data: FeaturedLink }) => {
    const { href, label, backgroundType, internalLink } = props.data

    return (
        <div className={cx(styles.link, backgroundType === 'black' ? styles.linkBlack : styles.linkWhite)}>
            <CrosshairLink
                as={internalLink ? 'internalLink' : 'externalLink'}
                href={href}
                label={label}
                colorVariant={backgroundType === 'black' ? 'light' : 'dark'}  />
        </div>
    )
}
